// src/components/CatatKehadiran.jsx
import { useState, useEffect, useMemo, useCallback } from 'react';
import { api } from '../services/api';

// Chip singkat supaya muat di layar HP untuk banyak kolom jam sekaligus.
const STATUS_OPTS = [
  { code: 'H', label: 'Hadir', color: 'bg-green-600 text-white' },
  { code: 'S', label: 'Sakit', color: 'bg-amber-500 text-white' },
  { code: 'I', label: 'Izin', color: 'bg-blue-500 text-white' },
  { code: 'A', label: 'Alpha', color: 'bg-red-600 text-white' },
  { code: 'T', label: 'Terlambat', color: 'bg-purple-500 text-white' },
  // Khusus siswa pindahan (baru masuk kelas ini belakangan) — dipakai untuk kolom jam
  // SEBELUM dia resmi terdaftar. Dihitung "sudah diisi" (tidak diblokir validasi),
  // tapi TIDAK ikut dihitung ke Hadir/Alpha/dst maupun ke persentase/nilai kehadiran.
  { code: 'TB', label: 'Tidak Berlaku', color: 'bg-slate-500 text-white' },
];

const JAM_OPTIONS = [2, 3]; // dalam sehari minimal 2 JP, maksimal 3 JP

export default function CatatKehadiran({ onBack }) {
  const [siswaAll, setSiswaAll] = useState([]);
  const [loadingSiswa, setLoadingSiswa] = useState(true);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  // toast: notifikasi mengambang yang selalu kelihatan (tidak tergantung posisi scroll)
  const [toast, setToast] = useState(null); // { type: 'success' | 'error', message }

  const [tanggal, setTanggal] = useState(() => new Date().toISOString().slice(0, 10));
  const [mataPelajaran, setMataPelajaran] = useState('');
  const [kelas, setKelas] = useState('');
  const [jumlahJam, setJumlahJam] = useState(3);

  // records: { [siswaId]: { catatan: string, jam: { [jamKe]: 'Hadir' | 'Sakit' | ... | '' } } }
  const [records, setRecords] = useState({});
  const [loadingKehadiran, setLoadingKehadiran] = useState(false);
  const [saving, setSaving] = useState(false);

  // panel aksi cepat: null | 'tambah' | 'hapus' | 'pindah'
  const [activePanel, setActivePanel] = useState(null);
  const [quickNama, setQuickNama] = useState('');
  const [quickNisn, setQuickNisn] = useState('');
  const [quickJk, setQuickJk] = useState('Laki-laki');
  const [quickSiswaId, setQuickSiswaId] = useState('');
  const [quickKelasTujuan, setQuickKelasTujuan] = useState('');
  const [quickSaving, setQuickSaving] = useState(false);

  const loadSiswaAll = useCallback(async () => {
    setLoadingSiswa(true);
    try {
      const res = await api.getSiswa('Semua Kelas');
      setSiswaAll((res.data || []).filter((s) => s.Status === 'Aktif'));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingSiswa(false);
    }
  }, []);

  useEffect(() => { loadSiswaAll(); }, [loadSiswaAll]);

  const kelasList = useMemo(
    () => Array.from(new Set(siswaAll.map((s) => s.Kelas).filter(Boolean))).sort(),
    [siswaAll]
  );

  const roster = useMemo(
    () => siswaAll.filter((s) => s.Kelas === kelas).sort((a, b) => String(a.Nama).localeCompare(String(b.Nama))),
    [siswaAll, kelas]
  );

  // Muat kehadiran yang sudah pernah disimpan untuk Tanggal+Kelas ini, lintas SEMUA jam.
  // Jam-jam yang sudah pernah diisi akan tampil terisi lagi; jam yang belum diisi tetap kosong
  // menunggu disusulkan.
  useEffect(() => {
    if (!tanggal || !kelas) {
      setRecords({});
      return;
    }
    (async () => {
      setLoadingKehadiran(true);
      try {
        const res = await api.getKehadiran(tanggal, kelas);
        const existing = {};
        let maxJam = 0;
        (res.data || []).forEach((r) => {
          const jamKe = Number(r.JamKe);
          if (!existing[r.ID_Siswa]) existing[r.ID_Siswa] = { catatan: '', jam: {} };
          existing[r.ID_Siswa].jam[jamKe] = r.Status;
          if (r.Catatan) existing[r.ID_Siswa].catatan = r.Catatan;
          if (jamKe > maxJam) maxJam = jamKe;
        });
        setRecords(existing);
        if (maxJam > 0) setJumlahJam((prev) => Math.max(prev, maxJam));
        if (res.data && res.data.length > 0 && res.data[0].MataPelajaran) {
          setMataPelajaran(res.data[0].MataPelajaran);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoadingKehadiran(false);
      }
    })();
  }, [tanggal, kelas]);

  function setStatus(id, jamKe, status) {
    setRecords((prev) => {
      const cur = prev[id] || { catatan: '', jam: {} };
      const already = cur.jam[jamKe] === status;
      return {
        ...prev,
        [id]: { ...cur, jam: { ...cur.jam, [jamKe]: already ? '' : status } },
      };
    });
  }
  function clearCell(id, jamKe) {
    setRecords((prev) => {
      const cur = prev[id] || { catatan: '', jam: {} };
      return { ...prev, [id]: { ...cur, jam: { ...cur.jam, [jamKe]: '' } } };
    });
  }
  function setCatatan(id, catatan) {
    setRecords((prev) => ({ ...prev, [id]: { catatan, jam: prev[id]?.jam || {} } }));
  }
  function isiSemuaHadir(jamKe) {
    setRecords((prev) => {
      const next = { ...prev };
      roster.forEach((s) => {
        const cur = next[s.ID] || { catatan: '', jam: {} };
        next[s.ID] = { ...cur, jam: { ...cur.jam, [jamKe]: 'Hadir' } };
      });
      return next;
    });
  }

  function showInfo(msg) {
    setInfo(msg);
    setTimeout(() => setInfo(''), 3000);
  }

  function showToast(type, message) {
    setToast({ type, message });
    setTimeout(() => setToast((prev) => (prev && prev.message === message ? null : prev)), 3500);
  }

  function togglePanel(name) {
    setActivePanel((prev) => (prev === name ? null : name));
    setQuickNama('');
    setQuickNisn('');
    setQuickJk('Laki-laki');
    setQuickSiswaId('');
    setQuickKelasTujuan('');
    setError('');
  }

  async function handleQuickTambah() {
    if (!quickNama.trim() || !quickNisn.trim()) {
      setError('Nama dan NISN wajib diisi.');
      return;
    }
    setQuickSaving(true);
    setError('');
    try {
      await api.addSiswa({ nama: quickNama.trim(), nisn: quickNisn.trim(), kelas, jenisKelamin: quickJk, status: 'Aktif' });
      showToast('success', `${quickNama} berhasil ditambahkan ke kelas ${kelas}.`);
      await loadSiswaAll();
      togglePanel(null);
    } catch (err) {
      showToast('error', `Gagal menambahkan siswa: ${err.message}`);
      setError(err.message);
    } finally {
      setQuickSaving(false);
    }
  }

  async function handleQuickHapus() {
    if (!quickSiswaId) {
      setError('Pilih siswa yang akan dihapus.');
      return;
    }
    const target = roster.find((s) => s.ID === quickSiswaId);
    if (!window.confirm(`Hapus PERMANEN ${target?.Nama}? Data tidak bisa dikembalikan.`)) return;
    setQuickSaving(true);
    setError('');
    try {
      await api.hapusSiswa([quickSiswaId]);
      showToast('success', `${target?.Nama} berhasil dihapus.`);
      await loadSiswaAll();
      togglePanel(null);
    } catch (err) {
      showToast('error', `Gagal menghapus siswa: ${err.message}`);
      setError(err.message);
    } finally {
      setQuickSaving(false);
    }
  }

  async function handleQuickPindah() {
    if (!quickSiswaId || !quickKelasTujuan) {
      setError('Pilih siswa dan kelas tujuan.');
      return;
    }
    const target = roster.find((s) => s.ID === quickSiswaId);
    setQuickSaving(true);
    setError('');
    try {
      await api.pindahKelas([quickSiswaId], quickKelasTujuan);
      showToast('success', `${target?.Nama} dipindahkan ke kelas ${quickKelasTujuan}.`);
      await loadSiswaAll();
      togglePanel(null);
    } catch (err) {
      showToast('error', `Gagal memindahkan siswa: ${err.message}`);
      setError(err.message);
    } finally {
      setQuickSaving(false);
    }
  }

  async function handleSimpan() {
    if (!mataPelajaran.trim()) {
      setError('Mata Pelajaran wajib diisi.');
      return;
    }
    if (roster.length === 0) {
      setError('Belum ada siswa aktif di kelas ini.');
      return;
    }

    const kolom = [];
    for (let jamKe = 1; jamKe <= jumlahJam; jamKe++) {
      const terisi = roster.filter((s) => records[s.ID]?.jam?.[jamKe]).length;
      if (terisi === 0) continue; // jam ini belum disentuh sama sekali -> lewati, tidak apa-apa
      if (terisi < roster.length) {
        setError(`Jam ke-${jamKe} baru terisi ${terisi} dari ${roster.length} siswa. Lengkapi dulu jam ini, atau kosongkan semua isiannya kalau memang belum waktunya diisi.`);
        return;
      }
      kolom.push({
        jamKe,
        records: roster.map((s) => ({
          idSiswa: s.ID,
          namaSiswa: s.Nama,
          status: records[s.ID].jam[jamKe],
          catatan: records[s.ID]?.catatan || '',
        })),
      });
    }

    if (kolom.length === 0) {
      setError('Belum ada jam yang diisi statusnya.');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const res = await api.saveKehadiran({ tanggal, kelas, mataPelajaran, kolom });
      showToast('success', `Kehadiran berhasil tersimpan (${kolom.length} jam, ${res.jumlah} data).`);
    } catch (err) {
      showToast('error', `Gagal menyimpan: ${err.message}`);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  const inputCls =
    'w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent';
  const labelCls = 'block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5';
  const quickBtnCls = 'text-xs font-medium px-3 py-2 rounded-lg transition border';

  return (
    <div className="min-h-screen bg-slate-50">
      {/* TOAST NOTIFIKASI */}
      {toast && (
        <div className="fixed bottom-5 left-1/2 -translate-x-1/2 z-50 max-w-[90vw]">
          <div
            className={`flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg text-sm font-medium text-white ${
              toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'
            }`}
          >
            <span aria-hidden="true">{toast.type === 'success' ? '✓' : '⚠'}</span>
            <span>{toast.message}</span>
          </div>
        </div>
      )}

      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex flex-wrap items-center justify-between gap-3">
          <div className="border-l-4 border-blue-600 pl-3">
            <h1 className="text-xl sm:text-2xl font-bold text-slate-800">Catat Kehadiran</h1>
            <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
              {kelas ? `${roster.length} siswa aktif di kelas ${kelas}` : 'Pilih kelas untuk mulai'}
            </p>
          </div>
          <button
            onClick={onBack}
            className="bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium px-4 py-2 rounded-lg transition"
          >
            &larr; Kembali ke Dashboard
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3">{error}</div>
        )}
        {info && (
          <div className="bg-green-50 border border-green-200 text-green-700 text-sm rounded-xl px-4 py-3">{info}</div>
        )}

        {/* PILIH TANGGAL, KELAS, JUMLAH JAM */}
        <section className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="bg-blue-600 px-5 sm:px-6 py-3.5">
            <h2 className="text-sm font-semibold text-white flex items-center gap-2">
              <span aria-hidden="true">&#128197;</span> Pilih Tanggal, Kelas &amp; Jumlah Jam
            </h2>
          </div>
          <div className="p-5 sm:p-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className={labelCls}>Tanggal</label>
                <input type="date" value={tanggal} onChange={(e) => setTanggal(e.target.value)} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Mata Pelajaran</label>
                <input
                  value={mataPelajaran}
                  onChange={(e) => setMataPelajaran(e.target.value)}
                  className={inputCls}
                  placeholder="Contoh: Sosiologi"
                />
              </div>
              <div>
                <label className={labelCls}>Kelas</label>
                <select value={kelas} onChange={(e) => setKelas(e.target.value)} className={inputCls}>
                  <option value="">-- Pilih Kelas --</option>
                  {kelasList.map((k) => (
                    <option key={k}>{k}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls}>Jumlah Jam Pertemuan Ini</label>
                <select
                  value={jumlahJam}
                  onChange={(e) => setJumlahJam(Number(e.target.value))}
                  disabled={!kelas}
                  className={inputCls}
                >
                  {JAM_OPTIONS.map((n) => (
                    <option key={n} value={n}>{n} Jam Pelajaran</option>
                  ))}
                </select>
              </div>
            </div>
            <p className="text-xs text-slate-400 mt-3">
              Tidak perlu bikin sesi lagi. Cukup isi kolom "Jam ke-" yang sedang berlangsung saja dan Simpan —
              kolom jam lain boleh dibiarkan kosong dan disusulkan nanti kalau jam pelajaran hari ini terpenggal istirahat.
            </p>
          </div>
        </section>

        {/* GRID KEHADIRAN */}
        {kelas && (
          <section className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="bg-indigo-600 px-5 sm:px-6 py-3.5">
              <h2 className="text-sm font-semibold text-white flex items-center gap-2">
                <span aria-hidden="true">&#128221;</span> Daftar Kehadiran
              </h2>
            </div>

            {/* LEGEND KODE STATUS */}
            <div className="flex flex-wrap gap-x-4 gap-y-1 px-4 sm:px-6 py-2.5 bg-slate-50 border-b border-slate-100 text-xs text-slate-500">
              {STATUS_OPTS.map((opt) => (
                <span key={opt.code}>
                  <strong className="text-slate-700">{opt.code}</strong> = {opt.label}
                </span>
              ))}
              <span><strong className="text-slate-700">&times;</strong> = Clear (kosongkan)</span>
            </div>

            {loadingSiswa || loadingKehadiran ? (
              <p className="text-center text-slate-500 py-10">Memuat data...</p>
            ) : roster.length === 0 ? (
              <p className="text-center text-slate-500 py-10">Belum ada siswa aktif di kelas ini.</p>
            ) : (
              <>
                {/* ===== VERSI HP (kartu bertumpuk, tombol lebih besar buat jempol) ===== */}
                <div className="sm:hidden divide-y divide-slate-100">
                  {roster.map((s) => {
                    const rec = records[s.ID] || { catatan: '', jam: {} };
                    return (
                      <div key={s.ID} className="p-4">
                        <p className="font-semibold text-slate-800 text-sm mb-3">{s.Nama}</p>
                        <div className="space-y-3">
                          {Array.from({ length: jumlahJam }, (_, i) => i + 1).map((jamKe) => {
                            const status = rec.jam[jamKe] || '';
                            return (
                              <div key={jamKe} className="bg-slate-50 rounded-xl p-3">
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                                    Jam ke-{jamKe}
                                  </span>
                                  <button
                                    onClick={() => isiSemuaHadir(jamKe)}
                                    className="text-[11px] font-medium text-green-700"
                                  >
                                    semua hadir
                                  </button>
                                </div>
                                <div className="grid grid-cols-4 gap-2">
                                  {STATUS_OPTS.map((opt) => (
                                    <button
                                      key={opt.code}
                                      onClick={() => setStatus(s.ID, jamKe, opt.label)}
                                      className={`text-xs font-bold h-11 rounded-lg transition ${
                                        status === opt.label ? opt.color : 'bg-white text-slate-500 border border-slate-200'
                                      }`}
                                    >
                                      {opt.code}
                                    </button>
                                  ))}
                                  <button
                                    onClick={() => clearCell(s.ID, jamKe)}
                                    className="text-xs font-bold h-11 rounded-lg bg-red-50 text-red-500 border border-red-100"
                                  >
                                    &times;
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        <input
                          value={rec.catatan}
                          onChange={(e) => setCatatan(s.ID, e.target.value)}
                          placeholder="Catatan (opsional)"
                          className="w-full mt-3 px-3 py-2.5 border border-slate-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    );
                  })}
                </div>

                {/* ===== VERSI TABLET/DESKTOP (grid, scroll horizontal kalau kolom jam banyak) ===== */}
                <div className="hidden sm:block overflow-x-auto">
                  <div style={{ minWidth: 260 + jumlahJam * 190 }}>
                    {/* Header kolom jam */}
                    <div
                      className="grid gap-2 px-4 sm:px-6 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wide border-b border-slate-100"
                      style={{ gridTemplateColumns: `180px repeat(${jumlahJam}, 190px) 1fr` }}
                    >
                      <span>Nama Siswa</span>
                      {Array.from({ length: jumlahJam }, (_, i) => i + 1).map((jamKe) => (
                        <div key={jamKe} className="flex items-center justify-between gap-1">
                          <span>Jam ke-{jamKe}</span>
                          <button
                            onClick={() => isiSemuaHadir(jamKe)}
                            className="normal-case text-[10px] font-medium text-green-700 hover:underline"
                            title={`Tandai semua Hadir untuk Jam ke-${jamKe}`}
                          >
                            semua hadir
                          </button>
                        </div>
                      ))}
                      <span>Catatan</span>
                    </div>

                    <div className="divide-y divide-slate-100">
                      {roster.map((s) => {
                        const rec = records[s.ID] || { catatan: '', jam: {} };
                        return (
                          <div
                            key={s.ID}
                            className="grid gap-2 px-4 sm:px-6 py-3 items-center"
                            style={{ gridTemplateColumns: `180px repeat(${jumlahJam}, 190px) 1fr` }}
                          >
                            <p className="font-semibold text-slate-800 text-sm">{s.Nama}</p>

                            {Array.from({ length: jumlahJam }, (_, i) => i + 1).map((jamKe) => {
                              const status = rec.jam[jamKe] || '';
                              return (
                                <div key={jamKe} className="grid grid-cols-3 gap-1 w-fit">
                                  {STATUS_OPTS.map((opt) => (
                                    <button
                                      key={opt.code}
                                      onClick={() => setStatus(s.ID, jamKe, opt.label)}
                                      title={opt.label}
                                      className={`text-[11px] font-bold w-8 h-8 rounded-lg transition ${
                                        status === opt.label ? opt.color : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                                      }`}
                                    >
                                      {opt.code}
                                    </button>
                                  ))}
                                  <button
                                    onClick={() => clearCell(s.ID, jamKe)}
                                    title="Clear"
                                    className="text-[11px] font-bold w-8 h-8 rounded-lg bg-red-50 text-red-500 hover:bg-red-100 transition"
                                  >
                                    &times;
                                  </button>
                                </div>
                              );
                            })}

                            <input
                              value={rec.catatan}
                              onChange={(e) => setCatatan(s.ID, e.target.value)}
                              placeholder="Catatan (opsional)"
                              className="min-w-[140px] px-3 py-1.5 border border-slate-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* TOOLBAR AKSI CEPAT + SIMPAN */}
            <div className="bg-slate-50 border-t border-slate-100 px-4 sm:px-6 py-4 flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => togglePanel('tambah')}
                  className={`${quickBtnCls} ${activePanel === 'tambah' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-blue-700 border-blue-200 hover:bg-blue-50'}`}
                >
                  + Tambah Siswa
                </button>
                <button
                  onClick={() => togglePanel('pindah')}
                  className={`${quickBtnCls} ${activePanel === 'pindah' ? 'bg-amber-600 text-white border-amber-600' : 'bg-white text-amber-700 border-amber-200 hover:bg-amber-50'}`}
                >
                  Pindahkan Kelas
                </button>
                <button
                  onClick={() => togglePanel('hapus')}
                  className={`${quickBtnCls} ${activePanel === 'hapus' ? 'bg-red-600 text-white border-red-600' : 'bg-white text-red-700 border-red-200 hover:bg-red-50'}`}
                >
                  Hapus Siswa
                </button>
              </div>
              <button
                onClick={handleSimpan}
                disabled={saving || roster.length === 0}
                className="bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white font-medium text-sm px-6 py-2.5 rounded-lg transition"
              >
                {saving ? 'Menyimpan...' : 'Simpan Kehadiran'}
              </button>
            </div>

            {/* PANEL TAMBAH SISWA */}
            {activePanel === 'tambah' && (
              <div className="border-t border-slate-100 bg-blue-50/50 px-4 sm:px-6 py-4">
                <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-3">
                  Tambah siswa baru ke kelas {kelas}
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <input value={quickNama} onChange={(e) => setQuickNama(e.target.value)} className={inputCls} placeholder="Nama Lengkap" />
                  <input value={quickNisn} onChange={(e) => setQuickNisn(e.target.value)} className={inputCls} placeholder="NISN" />
                  <select value={quickJk} onChange={(e) => setQuickJk(e.target.value)} className={inputCls}>
                    <option>Laki-laki</option>
                    <option>Perempuan</option>
                  </select>
                </div>
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={handleQuickTambah}
                    disabled={quickSaving}
                    className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded-lg transition"
                  >
                    {quickSaving ? 'Menyimpan...' : 'Tambah'}
                  </button>
                  <button onClick={() => togglePanel(null)} className="bg-white border border-slate-300 text-slate-600 text-sm font-medium px-4 py-2 rounded-lg transition">
                    Batal
                  </button>
                </div>
              </div>
            )}

            {/* PANEL PINDAHKAN KELAS */}
            {activePanel === 'pindah' && (
              <div className="border-t border-slate-100 bg-amber-50/50 px-4 sm:px-6 py-4">
                <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-3">Pindahkan siswa ke kelas lain</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <select value={quickSiswaId} onChange={(e) => setQuickSiswaId(e.target.value)} className={inputCls}>
                    <option value="">-- Pilih Siswa --</option>
                    {roster.map((s) => (
                      <option key={s.ID} value={s.ID}>{s.Nama}</option>
                    ))}
                  </select>
                  <select value={quickKelasTujuan} onChange={(e) => setQuickKelasTujuan(e.target.value)} className={inputCls}>
                    <option value="">-- Pindahkan ke Kelas --</option>
                    {kelasList.filter((k) => k !== kelas).map((k) => (
                      <option key={k}>{k}</option>
                    ))}
                  </select>
                </div>
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={handleQuickPindah}
                    disabled={quickSaving}
                    className="bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded-lg transition"
                  >
                    {quickSaving ? 'Memindahkan...' : 'Pindahkan'}
                  </button>
                  <button onClick={() => togglePanel(null)} className="bg-white border border-slate-300 text-slate-600 text-sm font-medium px-4 py-2 rounded-lg transition">
                    Batal
                  </button>
                </div>
              </div>
            )}

            {/* PANEL HAPUS SISWA */}
            {activePanel === 'hapus' && (
              <div className="border-t border-slate-100 bg-red-50/50 px-4 sm:px-6 py-4">
                <p className="text-xs font-semibold text-red-700 uppercase tracking-wide mb-3">Hapus siswa dari sistem (permanen)</p>
                <select value={quickSiswaId} onChange={(e) => setQuickSiswaId(e.target.value)} className={inputCls}>
                  <option value="">-- Pilih Siswa --</option>
                  {roster.map((s) => (
                    <option key={s.ID} value={s.ID}>{s.Nama}</option>
                  ))}
                </select>
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={handleQuickHapus}
                    disabled={quickSaving}
                    className="bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded-lg transition"
                  >
                    {quickSaving ? 'Menghapus...' : 'Hapus'}
                  </button>
                  <button onClick={() => togglePanel(null)} className="bg-white border border-slate-300 text-slate-600 text-sm font-medium px-4 py-2 rounded-lg transition">
                    Batal
                  </button>
                </div>
              </div>
            )}
          </section>
        )}
      </main>
    </div>
  );
}
